// Creator portal. Two states from one page:
//   - signed out: email form, hits /auth/clipper/magic-link/request
//   - signed in:  connection cards for TikTok + YouTube with connect/disconnect
//
// Uses the same studio-blue palette as the brand portal so the visual
// system stays single-sourced. Codex 2026-05-12.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '../../theme';
import { PublicNav } from '../../components/nav/PublicNav';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Turnstile } from '../../components/Turnstile';
import { clipperApi, type ClipperConnection, apiBase } from '../../lib/api';
import { CampaignsTab } from './CampaignsTab';
import { MyClipsTab } from './MyClipsTab';
import { EarningsTab } from './EarningsTab';
import { ConnectionsTab } from './ConnectionsTab';

const API_BASE = apiBase();

type SendState = 'idle' | 'sending' | 'sent' | 'rate_limited' | 'captcha_failed' | 'error';

export function Clipper() {
  const [params] = useSearchParams();
  const errorParam = params.get('error');
  const [me, setMe] = useState<{ email?: string; role?: string } | null | undefined>(undefined);
  const [connections, setConnections] = useState<ClipperConnection[] | null>(null);
  const [email, setEmail] = useState('');
  const [sendState, setSendState] = useState<SendState>('idle');
  const [debugLink, setDebugLink] = useState<string | null>(null);
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [tab, setTab] = useState<'campaigns' | 'clips' | 'earnings' | 'connections'>('campaigns');

  const onVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const onExpire = useCallback(() => setTurnstileToken(null), []);

  useEffect(() => {
    document.body.classList.add('studio-blue');
    return () => document.body.classList.remove('studio-blue');
  }, []);

  useEffect(() => {
    clipperApi.me().then((m) => setMe(m && m.role === 'creator' ? m : null));
  }, []);

  useEffect(() => {
    if (me?.role !== 'creator') return;
    clipperApi.connections().then((d) => setConnections(d.connections)).catch(() => setConnections([]));
  }, [me]);

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setSendState('sending');
    setDebugLink(null);
    try {
      const res = await clipperApi.requestMagicLink(email, turnstileToken ?? '');
      if (res.status === 403) {
        setSendState('captcha_failed');
        return;
      }
      if (res.status === 429) {
        setSendState('rate_limited');
        return;
      }
      if (res.status !== 200) {
        setSendState('error');
        return;
      }
      if (res.debug_link) setDebugLink(res.debug_link);
      setSendState('sent');
    } catch {
      setSendState('error');
    }
  }

  async function disconnect(provider: string) {
    setBusyProvider(provider);
    try {
      await clipperApi.disconnect(provider);
      const d = await clipperApi.connections();
      setConnections(d.connections);
    } finally {
      setBusyProvider(null);
    }
  }

  async function signout() {
    await clipperApi.signout();
    location.reload();
  }

  if (me === undefined) {
    return (
      <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: tokens.color.ink[500] }}>
        Loading...
      </main>
    );
  }

  if (!me) {
    return (
      <>
        <PublicNav />
        <main
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: tokens.color.paper[100],
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 460,
              padding: 48,
              background: tokens.color.paper['00'],
              borderRadius: 12,
              border: `1px solid ${tokens.color.line[100]}`,
            }}
          >
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.35em',
                textTransform: 'uppercase',
                color: tokens.color.ink[500],
              }}
            >
              Creator portal
            </span>
            <h1
              style={{
                fontFamily: tokens.font.display,
                fontSize: 40,
                fontWeight: 400,
                fontStyle: 'normal',
                marginTop: 12,
              }}
            >
              Connect your accounts.
            </h1>
            <p style={{ marginTop: 12, color: tokens.color.ink[500], fontSize: 14, lineHeight: 1.55 }}>
              Sign in to link your TikTok or YouTube so we can verify views on the clips you post.
              We'll email you a link. No password.
            </p>

            {errorParam && (
              <div
                style={{
                  marginTop: 20,
                  padding: 12,
                  background: 'rgba(212, 58, 47, 0.10)',
                  color: tokens.color.accent.rust,
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                {errorParam === 'invalid_or_expired'
                  ? 'That link was invalid or expired. Try again.'
                  : `Sign-in error: ${errorParam}.`}
              </div>
            )}

            <form onSubmit={submitEmail} style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Input
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={sendState === 'sending' || sendState === 'sent'}
                autoComplete="email"
              />
              <Turnstile onVerify={onVerify} onExpire={onExpire} />
              <Button type="submit" size="lg" loading={sendState === 'sending'} disabled={sendState === 'sent'}>
                {sendState === 'sent' ? 'Sent. Check your inbox.' : 'Email me a link'}
              </Button>
              {sendState === 'rate_limited' && (
                <p style={{ color: tokens.color.accent.rust, fontSize: 13 }}>
                  Too many requests. Try again in an hour.
                </p>
              )}
              {sendState === 'captcha_failed' && (
                <p style={{ color: tokens.color.accent.rust, fontSize: 13 }}>
                  Please complete the verification and try again.
                </p>
              )}
              {sendState === 'error' && (
                <p style={{ color: tokens.color.accent.rust, fontSize: 13 }}>
                  Something went wrong. Try again.
                </p>
              )}
            </form>

            {debugLink && (
              <div
                style={{
                  marginTop: 20,
                  padding: 16,
                  background: tokens.color.paper[100],
                  border: `1px dashed ${tokens.color.line[100]}`,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.25em',
                    textTransform: 'uppercase',
                    color: tokens.color.ink[500],
                    marginBottom: 8,
                  }}
                >
                  Dev fallback
                </div>
                <a
                  href={debugLink}
                  style={{
                    fontFamily: tokens.font.mono,
                    fontSize: 12,
                    color: tokens.color.ink[700],
                    wordBreak: 'break-all',
                    textDecoration: 'underline',
                  }}
                >
                  Click here to sign in
                </a>
              </div>
            )}
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <main style={{ minHeight: '100vh', padding: '48px 24px 64px', background: tokens.color.paper[100] }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontFamily: tokens.font.mono, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: tokens.color.ink[500] }}>Creator portal</span>
              <h1 style={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: 36, marginTop: 6 }}>Clip & earn</h1>
              <p style={{ marginTop: 6, color: tokens.color.ink[500], fontSize: 13 }}>{me.email}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={signout}>Sign out</Button>
          </header>
          <nav style={{ display: 'flex', gap: 24, borderBottom: `1px solid ${tokens.color.line[100]}`, marginBottom: 24 }}>
            {(['campaigns','clips','earnings','connections'] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 0', background: 'none', border: 'none', borderBottom: tab === t ? `2px solid ${tokens.color.ink[900]}` : '2px solid transparent', color: tab === t ? tokens.color.ink[900] : tokens.color.ink[500], fontWeight: 500, fontSize: 14, textTransform: 'capitalize', cursor: 'pointer' }}>
                {t === 'clips' ? 'My Clips' : t}
              </button>
            ))}
          </nav>
          {tab === 'campaigns' && <CampaignsTab />}
          {tab === 'clips' && <MyClipsTab />}
          {tab === 'earnings' && <EarningsTab />}
          {tab === 'connections' && <ConnectionsTab connections={connections} busyProvider={busyProvider} onDisconnect={disconnect} />}
        </div>
      </main>
    </>
  );
}

/**
 * Tiny intermediate page for /clipper/verify?token=... — bounces through
 * the worker callback so the cookie lands cleanly. Matches LoginVerify.
 */
export function ClipperVerify() {
  const [params] = useSearchParams();
  useEffect(() => {
    document.body.classList.add('studio-blue');
    const token = params.get('token');
    if (!token) return;
    window.location.href = `${API_BASE}/auth/clipper/magic-link/verify?token=${encodeURIComponent(
      token
    )}`;
    return () => document.body.classList.remove('studio-blue');
  }, [params]);
  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        fontFamily: tokens.font.display,
        fontStyle: 'normal',
        fontSize: 28,
        color: tokens.color.ink[500],
      }}
    >
      Signing you in...
    </main>
  );
}
